import { api } from '@/services/api';

export interface RadarItem {
  id: string;
  termo: string;
  tipo: string;
  data: string;
  dias_ate: number;
  horizonte: string;
  categoria: string;
  ancora: string;
  regra: string;
  /** Nulo quando a ocasião não tem antecedência medida — e isso é um estado
   *  legítimo, distinto de zero. A tela precisa saber diferenciar. */
  lead_time_dias: number | null;
  lead_time_semanas: number | null;
  lead_time_mediana_dias: number | null;
  pico_x_base: number | null;
  largura_dias: number | null;
  atraso_do_pico_dias: number | null;
  anos_medidos: number | null;
  series_respondentes: number | null;
  /** Assuntos que sobem junto com esta data — para quem lê poder julgar. */
  pautas_chave?: string | null;
  estrategia?: string | null;
  /** Era um `if` de duas saídas que chutava "Afiliados de Viagem" para tudo
   *  que não fosse compras. Sai nulo até haver dado real. */
  monetizacao_sugerida: string | null;
  sem_medicao_porque?: string | null;
  urgencia: 'imediata' | 'janela_aberta' | 'preparar' | 'no_horizonte' | 'sem_medicao';
  status_janela: string;
}

export interface RadarResponse {
  mes?: number;
  escopo?: string;
  total_pautas: number;
  items: RadarItem[];
  proveniencia?: Record<string, string>;
}

export interface CycleItem {
  termo: string;
  lead_time_dias_recomendado: number;
  lead_time_mediana_dias: number;
  lead_time_semanas_ignicao: number;
  pico_x_base: number;
  largura_dias: number;
  atraso_do_pico_dias: number;
  anos_medidos: number;
  ignicao_anos_acesos: number;
  series_respondentes: number;
  p_especificidade: number;
  mesma_data_que: string | null;
  ancora: string;
  categoria_contextual: string;
  horizonte_temporal: string;
}

export interface SpecialWeekItem {
  semana_num: number;
  nome_semana: string;
  mes_referencia: number;
  total_ocasioes: number;
  maior_pico: number | null;
  maior_ignicao_dias: number | null;
}

export interface DayOfWeekItem {
  dia_semana: number;
  dia_da_semana: string;
  /** Quanto o dia rende contra a média do próprio assunto. Medido, não opinado:
   *  substituiu um "perfil psicológico" digitado à mão para cada dia. */
  indice_vs_media: number;
  variacao_pct: number;
}

export interface PredictiveSettings {
  enabled: boolean;
  selected_regions: string[];
  active_categories: string[];
  affiliate_amazon_tag?: string;
  affiliate_shopee_id?: string;
  affiliate_booking_url?: string;
  affiliate_decolar_url?: string;
  lead_time_sensitivity: 'agressivo' | 'padrao' | 'conservador';
  auto_create_kanban_drafts: boolean;
  last_sync?: string;
}

export interface GenerateDraftPayload {
  termo: string;
  categoria: string;
  lead_time_dias: number;
  insert_affiliates: boolean;
  site_destino?: string;
  custom_title?: string;
}

export interface GenerateDraftResponse {
  status: string;
  story_id: string;
  title: string;
  site: string;
  category: string;
  content_preview: string;
  message: string;
}

export const predictiveCalendarService = {
  getRadar: (mes?: number, uf?: string) => {
    const params = new URLSearchParams();
    if (mes) params.append('mes', String(mes));
    if (uf) params.append('uf', uf);
    const qs = params.toString();
    return api.get<RadarResponse>(`/predictive-calendar/radar${qs ? `?${qs}` : ''}`);
  },

  getCycles: () => api.get<{ total: number; cycles: CycleItem[] }>('/predictive-calendar/cycles'),

  getSpecialWeeks: () =>
    api.get<{ total: number; weeks: SpecialWeekItem[] }>('/predictive-calendar/special-weeks'),

  getDayOfWeek: () =>
    api.get<{ total: number; days: DayOfWeekItem[] }>('/predictive-calendar/day-of-week'),

  /** A rota /regional saiu: o serviço declarava um filtro por praça que nunca
   *  usou, e esta tela já descartava a resposta (`const [, setRegional]`).
   *  Enquanto não houver atenção medida por região, não há o que servir. */

  // O `?client=` morreu: o backend resolve o tenant pela SESSÃO (F-08/F-39).
  // Mandar slug daqui era, na melhor hipótese, ruído — e na pior, a porta do
  // vazamento cross-tenant que a auditoria mediu.
  getSettings: () => api.get<PredictiveSettings>('/predictive-calendar/settings'),

  updateSettings: (payload: PredictiveSettings) =>
    api.put<{ status: string; settings: PredictiveSettings }>(
      '/predictive-calendar/settings',
      payload,
    ),

  generateDraft: (payload: GenerateDraftPayload) =>
    api.post<GenerateDraftResponse>('/predictive-calendar/generate-draft', payload),
};
